import React, { useMemo, useState } from 'react';
import './SchoolComparison.css';
import hbsLogo from '../assets/HBS_LOGO.png';
import stanfordLogo from '../assets/STANFORD_LOGO.png';
import whartonLogo from '../assets/WHARTON_LOGO.png';
import mitLogo from '../assets/MIT_LOGO.png';
import chicagoBoothLogo from '../assets/CHICAGO_BOOTH_LOGO.png';
import kelloggLogo from '../assets/KELLOGG_LOGO.png';
import yaleSomLogo from '../assets/YALE_SOM_LOGO.png';
import columbiaLogo from '../assets/COLUMBIA_LOGO.png';
import berkeleyHaasLogo from '../assets/BERKELEY_HAAS_LOGO.png';
import dardenLogo from '../assets/DARDEN_LOGO.png';
import uclaAndersonLogo from '../assets/UCLA_ANDERSON_LOGO.png';
import emoryGoizuetaLogo from '../assets/EMORY_GOIZUETA_LOGO.png';

type DeferredProgram = {
  id: string;
  school: string;
  programName: string;
  typicalAudience: string;
  deferralWindow: string;
  gmatGre: string;
  applicationTiming: string;
  interview: string;
  notes: string[];
};

type MbaAdmissionsStats2024 = {
  acceptanceRatePct: number;
  gmat: number;
  gpa: number;
  gre: number | null;
  classSize: number;
  womenPct: number;
  internationalPct: number;
};

const PROGRAMS: DeferredProgram[] = [
  {
    id: 'hbs-2plus2',
    school: 'Harvard Business School',
    programName: '2+2 Program',
    typicalAudience: 'College seniors and early-career professionals (HBS-defined eligibility)',
    deferralWindow: 'Typically 2–4 years of work experience before MBA start',
    gmatGre: 'GMAT or GRE accepted',
    applicationTiming: 'Annual cycle; deadlines vary by year',
    interview: 'By invitation',
    notes: [
      'Strong emphasis on leadership and impact',
      'Verify eligibility and deadlines on the official program page each year'
    ]
  },
  {
    id: 'wharton-moelis',
    school: 'The Wharton School',
    programName: 'Moelis Advance Access Program',
    typicalAudience: 'College seniors in their final undergraduate year',
    deferralWindow: 'Work full-time for 2–4 years before MBA start',
    gmatGre: 'GMAT or GRE accepted',
    applicationTiming: 'Annual cycle; deadlines vary by year',
    interview: 'By invitation',
    notes: [
      'Focus on academic excellence and leadership',
      'Check the program website for eligibility details and timelines'
    ]
  },
  {
    id: 'stanford-deferred',
    school: 'Stanford GSB',
    programName: 'Deferred Enrollment',
    typicalAudience: 'College seniors and master’s students with limited full-time experience',
    deferralWindow: 'Defer MBA start by up to ~4 years (program-dependent)',
    gmatGre: 'GMAT or GRE accepted',
    applicationTiming: 'Annual cycle; deadlines vary by year',
    interview: 'By invitation',
    notes: [
      'Very selective; clarity of purpose and personal narrative matters',
      'Confirm exact deferral rules and eligibility for your degree program'
    ]
  },
  {
    id: 'mit-sloan-early-admission',
    school: 'MIT Sloan',
    programName: 'MBA Early Admission',
    typicalAudience: 'College seniors and students in select graduate programs',
    deferralWindow: 'Defer MBA start for a set period (program-defined)',
    gmatGre: 'GMAT or GRE accepted',
    applicationTiming: 'Annual cycle; deadlines vary by year',
    interview: 'By invitation',
    notes: [
      'Strong fit with analytical/innovation-oriented applicants',
      'Review eligibility requirements carefully'
    ]
  },
  {
    id: 'booth-scholars',
    school: 'Chicago Booth',
    programName: 'Booth Scholars Program',
    typicalAudience: 'College seniors and students in select graduate programs',
    deferralWindow: 'Typically 2–4 years before MBA start (program-defined)',
    gmatGre: 'GMAT or GRE accepted (confirm current policy)',
    applicationTiming: 'Annual cycle; deadlines vary by year',
    interview: 'By invitation',
    notes: [
      'Designed for high-potential candidates planning full-time work experience before enrolling',
      'Confirm eligibility by degree program and graduation timing on the official site'
    ]
  },
  {
    id: 'kellogg-deferred',
    school: 'Kellogg SOM',
    programName: 'Deferred Enrollment / Deferred MBA',
    typicalAudience: 'College seniors (and/or eligible graduate students; confirm eligibility)',
    deferralWindow: 'Defer MBA start for a set period (program-defined)',
    gmatGre: 'GMAT or GRE accepted (confirm current policy)',
    applicationTiming: 'Annual cycle; deadlines vary by year',
    interview: 'By invitation',
    notes: [
      'Kellogg’s deferred option can have specific eligibility requirements—verify current details',
      'Use the official program page for the latest deadlines and required materials'
    ]
  },
  {
    id: 'yale-silver-scholars',
    school: 'Yale SOM',
    programName: 'Silver Scholars Program',
    typicalAudience: 'College seniors in their final undergraduate year',
    deferralWindow: 'Enroll directly after graduation (traditional “deferred” differs; verify fit)',
    gmatGre: 'GMAT or GRE accepted',
    applicationTiming: 'Annual cycle; deadlines vary by year',
    interview: 'By invitation',
    notes: [
      'Not a deferral program in the classic sense—structured for immediate post-college enrollment',
      'Verify program structure and timeline on the official Yale SOM Silver Scholars page'
    ]
  },
  {
    id: 'columbia-deferred',
    school: 'Columbia Business School',
    programName: 'Deferred Enrollment',
    typicalAudience: 'College seniors and eligible graduate students',
    deferralWindow: 'Program-defined deferral period before MBA start',
    gmatGre: 'GMAT or GRE accepted (confirm current policy)',
    applicationTiming: 'Annual cycle; deadlines vary by year',
    interview: 'By invitation',
    notes: [
      'Confirm exact deferred-enrollment eligibility and timeline on the official CBS site',
      'Highlight leadership and career clarity in application materials'
    ]
  },
  {
    id: 'berkeley-haas-deferred',
    school: 'Berkeley Haas',
    programName: 'Deferred Enrollment',
    typicalAudience: 'College seniors and eligible graduate students',
    deferralWindow: 'Program-defined deferral period before MBA start',
    gmatGre: 'GMAT or GRE accepted (confirm current policy)',
    applicationTiming: 'Annual cycle; deadlines vary by year',
    interview: 'By invitation',
    notes: [
      'Verify current deferred option details and requirements with Berkeley Haas',
      'Strong fit narrative around leadership and innovation is important'
    ]
  },
  {
    id: 'uva-darden-deferred',
    school: 'UVA Darden',
    programName: 'Deferred Enrollment',
    typicalAudience: 'College seniors and eligible advanced-degree students',
    deferralWindow: 'Program-defined deferral period before MBA start',
    gmatGre: 'GMAT or GRE accepted (confirm current policy)',
    applicationTiming: 'Annual cycle; deadlines vary by year',
    interview: 'By invitation',
    notes: [
      'Check Darden deferred-admissions eligibility and deadline updates each year',
      'Show collaborative leadership and clear career intent'
    ]
  },
  {
    id: 'ucla-anderson-deferred',
    school: 'UCLA',
    programName: 'Deferred Enrollment',
    typicalAudience: 'College seniors and eligible applicants with limited full-time experience',
    deferralWindow: 'Program-defined deferral period before MBA start',
    gmatGre: 'GMAT or GRE accepted (confirm current policy)',
    applicationTiming: 'Annual cycle; deadlines vary by year',
    interview: 'By invitation',
    notes: [
      'Verify current deferred enrollment details on UCLA Anderson official page',
      'Demonstrate leadership trajectory and strong program fit'
    ]
  },
  {
    id: 'emory-goizueta-deferred',
    school: 'Emory Goizueta',
    programName: 'Deferred Enrollment',
    typicalAudience: 'College seniors and eligible applicants',
    deferralWindow: 'Program-defined deferral period before MBA start',
    gmatGre: 'GMAT or GRE accepted (confirm current policy)',
    applicationTiming: 'Annual cycle; deadlines vary by year',
    interview: 'By invitation',
    notes: [
      'Review Emory Goizueta deferred-admissions criteria and timelines annually',
      'Emphasize leadership impact and community contribution'
    ]
  }
];

const MAX_COMPARE = 3;
const DASH = '—';

/** Short label for column/card headers (school only; program row still lists full program name). */
const columnHeaderLabel = (p: DeferredProgram | undefined, slotIndex: number) =>
  p ? p.school : `Program ${slotIndex + 1}`;

const EMPTY_PROGRAM: DeferredProgram = {
  id: '',
  school: '',
  programName: '',
  typicalAudience: '',
  deferralWindow: '',
  gmatGre: '',
  applicationTiming: '',
  interview: '',
  notes: []
};

const PROGRAM_LOGO_BY_ID: Record<string, string> = {
  'hbs-2plus2': hbsLogo,
  'stanford-deferred': stanfordLogo,
  'wharton-moelis': whartonLogo,
  'mit-sloan-early-admission': mitLogo,
  'booth-scholars': chicagoBoothLogo,
  'kellogg-deferred': kelloggLogo,
  'yale-silver-scholars': yaleSomLogo,
  'columbia-deferred': columbiaLogo,
  'berkeley-haas-deferred': berkeleyHaasLogo,
  'uva-darden-deferred': dardenLogo,
  'ucla-anderson-deferred': uclaAndersonLogo,
  'emory-goizueta-deferred': emoryGoizuetaLogo
};

const MBA_ADMISSIONS_2024: Record<string, MbaAdmissionsStats2024> = {
  'Harvard Business School': {
    acceptanceRatePct: 11.2,
    gmat: 740,
    gpa: 3.7,
    gre: 326,
    classSize: 930,
    womenPct: 45,
    internationalPct: 35
  },
  'The Wharton School': {
    acceptanceRatePct: 20.5,
    gmat: 732,
    gpa: 3.7,
    gre: 325,
    classSize: 866,
    womenPct: 47,
    internationalPct: 31
  },
  'Stanford GSB': {
    acceptanceRatePct: 6.8,
    gmat: 738,
    gpa: 3.8,
    gre: 327,
    classSize: 424,
    womenPct: 44,
    internationalPct: 39
  },
  'Chicago Booth': {
    acceptanceRatePct: 28.7,
    gmat: 729,
    gpa: 3.6,
    gre: 324,
    classSize: 632,
    womenPct: 42,
    internationalPct: 35
  },
  'Kellogg SOM': {
    acceptanceRatePct: 28.6,
    gmat: 733,
    gpa: 3.7,
    gre: 325,
    classSize: 524,
    womenPct: 50,
    internationalPct: 40
  },
  'MIT Sloan': {
    acceptanceRatePct: 14.1,
    gmat: 730,
    gpa: 3.7,
    gre: null,
    classSize: 433,
    womenPct: 49,
    internationalPct: 40
  },
  'Yale SOM': {
    acceptanceRatePct: 17,
    gmat: 730,
    gpa: 3.7,
    gre: 325,
    classSize: 350,
    womenPct: 47,
    internationalPct: 35
  }
};

const WHY_THIS_SCHOOL: Record<string, string> = {
  'Harvard Business School':
    'HBS is a high-intensity, general management environment built around the case method and a tight section community. It’s best for candidates who want to lead early, learn by debate, and thrive in a culture that rewards initiative, clarity of thinking, and peer influence.',
  'The Wharton School':
    'Wharton pairs a highly collaborative culture with deep analytical rigor and unmatched breadth in finance and entrepreneurship. It’s a strong fit for candidates who want flexibility to explore, value data-driven decision making, and plan to leverage a large, global network.',
  'Stanford GSB':
    'Stanford emphasizes self-reflection, purpose, and personal leadership growth within a close-knit community. It’s best for candidates who have a clear “why,” want to expand their impact through innovation, and prefer an intimate program with outsized access to faculty and peers.',
  'Chicago Booth':
    'Booth is known for intellectual freedom, analytical depth, and a culture that encourages challenging assumptions. It’s ideal for candidates who want to tailor their curriculum, love rigorous debate, and are motivated by building conviction through evidence and first principles.',
  'Kellogg SOM':
    'Kellogg is a team-first, relationship-driven program with standout strengths in marketing, leadership, and collaborative learning. It’s a great fit for candidates who lead through empathy, enjoy group problem-solving, and want a warm, community-centric culture.',
  'MIT Sloan':
    'MIT Sloan blends analytics with practical innovation in a hands-on, builder-oriented culture. It’s best for candidates who want to turn ideas into systems, work across disciplines, and learn in an environment that prizes experimentation, data, and real-world impact.',
  'Yale SOM':
    'Yale SOM is mission-driven and integrated with a broader university ecosystem, with a strong focus on stakeholder leadership. It’s a great match for candidates who care about values and purpose, want cross-campus opportunities, and plan to lead in complex, multi-stakeholder settings.'
  ,
  'Columbia Business School':
    'Columbia offers a fast-moving, career-connected MBA experience in New York City with strong access to finance, consulting, and entrepreneurship ecosystems. It is a strong fit for applicants who value real-time market exposure and a practical, execution-oriented network.',
  'Berkeley Haas':
    'Berkeley Haas combines analytical rigor with an innovation-driven, values-based culture. It is a great fit for candidates who want to lead in technology, entrepreneurship, and impact-oriented environments while staying grounded in collaborative leadership principles.',
  'UVA Darden':
    'UVA Darden emphasizes case-method intensity, communication clarity, and principled leadership. It suits applicants who want to sharpen decision-making under ambiguity and grow in a tight-knit, high-engagement classroom environment.',
  'UCLA':
    'UCLA Anderson offers strong exposure to technology, entertainment, and entrepreneurship with a collaborative West Coast culture. It is well suited for candidates who want flexible career paths and practical leadership development in innovation-heavy industries.',
  'Emory Goizueta':
    'Emory Goizueta blends personalized leadership development with strong general management training and deep ties to major business hubs. It is a strong fit for candidates who value close-knit learning and practical career acceleration.'
};

const getAdmissionsStatsForSchool = (school: string): MbaAdmissionsStats2024 | null => {
  return MBA_ADMISSIONS_2024[school] ?? null;
};

type MetricDirection = 'higher-better' | 'lower-better';

const getHighlightClassesForNumericMetric = (
  values: Array<number | null | undefined>,
  direction: MetricDirection
) => {
  const present = values
    .map((v, idx) => ({ v: typeof v === 'number' ? v : null, idx }))
    .filter((x) => x.v !== null) as Array<{ v: number; idx: number }>;

  const classes = values.map(() => '');
  if (present.length < 2) return classes;

  let bestValue = present[0].v;
  let worstValue = present[0].v;
  for (const x of present) {
    bestValue = direction === 'higher-better' ? Math.max(bestValue, x.v) : Math.min(bestValue, x.v);
    worstValue = direction === 'higher-better' ? Math.min(worstValue, x.v) : Math.max(worstValue, x.v);
  }

  const bestCount = present.filter((x) => x.v === bestValue).length;
  const worstCount = present.filter((x) => x.v === worstValue).length;

  for (const x of present) {
    if (bestCount === 1 && x.v === bestValue) classes[x.idx] = 'cell-best';
    else if (worstCount === 1 && x.v === worstValue) classes[x.idx] = 'cell-worst';
  }

  return classes;
};

const getDiffClassesForText = (values: Array<string | null | undefined>) => {
  const normalized = values.map((v) => (v ?? '').trim());
  const nonEmpty = normalized.filter(Boolean);
  if (nonEmpty.length < 2) return values.map(() => '');
  const allSame = nonEmpty.every((v) => v === nonEmpty[0]);
  return values.map(() => (allSame ? '' : 'cell-diff'));
};

const formatPercent = (value: number | null | undefined) => {
  if (typeof value !== 'number') return DASH;
  return `${value.toFixed(value % 1 === 0 ? 0 : 1)}%`;
};

const formatNumber = (value: number | null | undefined) => {
  if (typeof value !== 'number') return DASH;
  return value.toString();
};

const formatGpa = (value: number | null | undefined) => {
  if (typeof value !== 'number') return DASH;
  return value.toFixed(2);
};

const SchoolComparison: React.FC = () => {
  const [selectedProgramIds, setSelectedProgramIds] = useState<string[]>([
    'hbs-2plus2',
    'stanford-deferred',
    'wharton-moelis'
  ]);

  const selectedIds = useMemo<string[]>(() => {
    return selectedProgramIds;
  }, [selectedProgramIds]);

  const handleToggleProgram = (programId: string) => {
    setSelectedProgramIds((prev) => {
      if (prev.includes(programId)) {
        return prev.filter((id) => id !== programId);
      }
      if (prev.length >= MAX_COMPARE) return prev;
      return [...prev, programId];
    });
  };


  const format = (value: string | undefined | null) => {
    const v = (value ?? '').toString().trim();
    return v ? v : DASH;
  };

  const formatNotes = (notes: string[] | undefined | null) => {
    const cleaned = (notes ?? []).map((n) => n.trim()).filter(Boolean);
    if (cleaned.length === 0) return [DASH];
    return cleaned;
  };

  const selectedProgramsBySlot = useMemo(() => {
    return selectedIds.map((id) => PROGRAMS.find((p) => p.id === id) ?? EMPTY_PROGRAM);
  }, [selectedIds]);

  const selectedStatsBySlot = useMemo(() => {
    return selectedProgramsBySlot.map((p) => (p ? getAdmissionsStatsForSchool(p.school) : null));
  }, [selectedProgramsBySlot]);

  const acceptanceHighlights = useMemo(() => {
    return getHighlightClassesForNumericMetric(
      selectedStatsBySlot.map((s) => s?.acceptanceRatePct),
      'lower-better'
    );
  }, [selectedStatsBySlot]);

  const gmatHighlights = useMemo(() => {
    return getHighlightClassesForNumericMetric(
      selectedStatsBySlot.map((s) => s?.gmat),
      'higher-better'
    );
  }, [selectedStatsBySlot]);

  const gpaHighlights = useMemo(() => {
    return getHighlightClassesForNumericMetric(
      selectedStatsBySlot.map((s) => s?.gpa),
      'higher-better'
    );
  }, [selectedStatsBySlot]);

  const textDiffTypicalAudience = useMemo(() => {
    return getDiffClassesForText(selectedProgramsBySlot.map((p) => p?.typicalAudience));
  }, [selectedProgramsBySlot]);

  const textDiffDeferralWindow = useMemo(() => {
    return getDiffClassesForText(selectedProgramsBySlot.map((p) => p?.deferralWindow));
  }, [selectedProgramsBySlot]);

  const textDiffTestPolicy = useMemo(() => {
    return getDiffClassesForText(selectedProgramsBySlot.map((p) => p?.gmatGre));
  }, [selectedProgramsBySlot]);

  const textDiffApplicationTiming = useMemo(() => {
    return getDiffClassesForText(selectedProgramsBySlot.map((p) => p?.applicationTiming));
  }, [selectedProgramsBySlot]);

  const textDiffInterview = useMemo(() => {
    return getDiffClassesForText(selectedProgramsBySlot.map((p) => p?.interview));
  }, [selectedProgramsBySlot]);

  return (
    <div className="school-comparison-page">
      <div className="school-comparison-container">
        <div className="school-comparison-header">
          <h1 className="school-comparison-title">School Comparison</h1>
          <p className="school-comparison-subtitle">
            Compare deferred MBA programs side-by-side. Select up to {MAX_COMPARE}.
          </p>
        </div>

        <div className="school-comparison-controls">
          <div className="school-comparison-picker" aria-label="Program selection">
            <div className="picker-header">
              <div className="picker-title">Programs to compare</div>
              <div className="picker-hint">{selectedProgramIds.length}/{MAX_COMPARE} selected</div>
            </div>

            <div className="picker-logo-grid" role="list" aria-label="Program logo selection">
              {PROGRAMS.map((p) => {
                const isSelected = selectedProgramIds.includes(p.id);
                const atLimit = !isSelected && selectedProgramIds.length >= MAX_COMPARE;
                const logo = PROGRAM_LOGO_BY_ID[p.id];
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`picker-logo-button ${isSelected ? 'selected' : ''}`}
                    disabled={atLimit}
                    onClick={() => handleToggleProgram(p.id)}
                    title={`${p.school} — ${p.programName}`}
                    aria-pressed={isSelected}
                  >
                    <span className="picker-logo-media">
                      {logo ? (
                        <img src={logo} alt="" className="picker-logo-image" />
                      ) : (
                        <span className="picker-logo-fallback">{p.school.split(' ').slice(0, 2).join(' ')}</span>
                      )}
                    </span>
                    <span className="picker-logo-text">
                      {p.school}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="school-comparison-disclaimer" role="note" aria-label="Data disclaimer">
          Admissions metrics below reflect general MBA admissions (2024) and may vary for deferred programs. Please
          verify the latest details on each program’s official website.
        </div>

        <div className="comparison-table-wrap">
          <table className="comparison-table">
            <caption className="sr-only">Deferred MBA program comparison</caption>
            <thead>
              <tr>
                <th scope="col" className="comparison-sticky-col">
                  Attribute
                </th>
                {selectedIds.map((id, idx) => {
                  const p = id ? PROGRAMS.find((x) => x.id === id) : undefined;
                  const header = columnHeaderLabel(p, idx);
                  return (
                    <th key={`h-${idx}`} scope="col">
                      {header}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <tr className="comparison-section">
                <th colSpan={selectedIds.length + 1} scope="colgroup" className="comparison-section-cell">
                  MBA Admissions Snapshot (2024)
                </th>
              </tr>
              <tr>
                <th scope="row" className="comparison-sticky-col">
                  Acceptance rate (lower = more selective)
                </th>
                {selectedStatsBySlot.map((s, idx) => (
                  <td key={`acc-${idx}`} className={acceptanceHighlights[idx]}>
                    {formatPercent(s?.acceptanceRatePct)}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row" className="comparison-sticky-col">
                  GMAT (higher is better)
                </th>
                {selectedStatsBySlot.map((s, idx) => (
                  <td key={`gmat-${idx}`} className={gmatHighlights[idx]}>
                    {formatNumber(s?.gmat)}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row" className="comparison-sticky-col">
                  GPA (higher is better)
                </th>
                {selectedStatsBySlot.map((s, idx) => (
                  <td key={`gpa-${idx}`} className={gpaHighlights[idx]}>
                    {formatGpa(s?.gpa)}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row" className="comparison-sticky-col">
                  GRE
                </th>
                {selectedStatsBySlot.map((s, idx) => (
                  <td key={`gre-${idx}`}>{formatNumber(s?.gre)}</td>
                ))}
              </tr>
              <tr>
                <th scope="row" className="comparison-sticky-col">
                  Class size
                </th>
                {selectedStatsBySlot.map((s, idx) => (
                  <td key={`size-${idx}`}>{formatNumber(s?.classSize)}</td>
                ))}
              </tr>
              <tr>
                <th scope="row" className="comparison-sticky-col">
                  Women
                </th>
                {selectedStatsBySlot.map((s, idx) => (
                  <td key={`women-${idx}`}>{formatPercent(s?.womenPct)}</td>
                ))}
              </tr>
              <tr>
                <th scope="row" className="comparison-sticky-col">
                  International
                </th>
                {selectedStatsBySlot.map((s, idx) => (
                  <td key={`intl-${idx}`}>{formatPercent(s?.internationalPct)}</td>
                ))}
              </tr>

              <tr className="comparison-section">
                <th colSpan={selectedIds.length + 1} scope="colgroup" className="comparison-section-cell">
                  Deferred Program Details
                </th>
              </tr>
              <tr>
                <th scope="row" className="comparison-sticky-col">
                  School
                </th>
                {selectedIds.map((id, idx) => {
                  const p = id ? PROGRAMS.find((x) => x.id === id) : undefined;
                  return <td key={`school-${idx}`}>{format(p?.school)}</td>;
                })}
              </tr>
              <tr>
                <th scope="row" className="comparison-sticky-col">
                  Program
                </th>
                {selectedIds.map((id, idx) => {
                  const p = id ? PROGRAMS.find((x) => x.id === id) : undefined;
                  return <td key={`prog-${idx}`}>{format(p?.programName)}</td>;
                })}
              </tr>
              <tr>
                <th scope="row" className="comparison-sticky-col">
                  Typical audience
                </th>
                {selectedIds.map((id, idx) => {
                  const p = id ? PROGRAMS.find((x) => x.id === id) : undefined;
                  return (
                    <td key={`aud-${idx}`} className={textDiffTypicalAudience[idx]}>
                      {format(p?.typicalAudience)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <th scope="row" className="comparison-sticky-col">
                  Deferral window
                </th>
                {selectedIds.map((id, idx) => {
                  const p = id ? PROGRAMS.find((x) => x.id === id) : undefined;
                  return (
                    <td key={`def-${idx}`} className={textDiffDeferralWindow[idx]}>
                      {format(p?.deferralWindow)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <th scope="row" className="comparison-sticky-col">
                  Test policy
                </th>
                {selectedIds.map((id, idx) => {
                  const p = id ? PROGRAMS.find((x) => x.id === id) : undefined;
                  return (
                    <td key={`test-${idx}`} className={textDiffTestPolicy[idx]}>
                      {format(p?.gmatGre)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <th scope="row" className="comparison-sticky-col">
                  Application timing
                </th>
                {selectedIds.map((id, idx) => {
                  const p = id ? PROGRAMS.find((x) => x.id === id) : undefined;
                  return (
                    <td key={`timing-${idx}`} className={textDiffApplicationTiming[idx]}>
                      {format(p?.applicationTiming)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <th scope="row" className="comparison-sticky-col">
                  Interview
                </th>
                {selectedIds.map((id, idx) => {
                  const p = id ? PROGRAMS.find((x) => x.id === id) : undefined;
                  return (
                    <td key={`int-${idx}`} className={textDiffInterview[idx]}>
                      {format(p?.interview)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <th scope="row" className="comparison-sticky-col">
                  Notes
                </th>
                {selectedIds.map((id, idx) => {
                  const p = id ? PROGRAMS.find((x) => x.id === id) : undefined;
                  const notes = formatNotes(p?.notes);
                  return (
                    <td key={`notes-${idx}`}>
                      <ul className="comparison-notes-list">
                        {notes.map((n) => (
                          <li key={n}>{n}</li>
                        ))}
                      </ul>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>

        <div className="comparison-mobile" aria-label="Mobile comparison view">
          {selectedProgramsBySlot.map((p, idx) => {
            const stats = selectedStatsBySlot[idx];
            const header = columnHeaderLabel(p, idx);
            return (
              <section key={`m-${idx}`} className="mobile-card" aria-label={header}>
                <header className="mobile-card-header">
                  <div className="mobile-card-title">{header}</div>
                </header>

                <div className="mobile-card-body">
                  <div className="mobile-section-title">MBA Admissions Snapshot (2024)</div>

                  <div className="mobile-row">
                    <div className="mobile-label">Acceptance rate (lower = more selective)</div>
                    <div className={`mobile-value ${acceptanceHighlights[idx]}`}>{formatPercent(stats?.acceptanceRatePct)}</div>
                  </div>
                  <div className="mobile-row">
                    <div className="mobile-label">GMAT</div>
                    <div className={`mobile-value ${gmatHighlights[idx]}`}>{formatNumber(stats?.gmat)}</div>
                  </div>
                  <div className="mobile-row">
                    <div className="mobile-label">GPA</div>
                    <div className={`mobile-value ${gpaHighlights[idx]}`}>{formatGpa(stats?.gpa)}</div>
                  </div>
                  <div className="mobile-row">
                    <div className="mobile-label">GRE</div>
                    <div className="mobile-value">{formatNumber(stats?.gre)}</div>
                  </div>
                  <div className="mobile-row">
                    <div className="mobile-label">Class size</div>
                    <div className="mobile-value">{formatNumber(stats?.classSize)}</div>
                  </div>
                  <div className="mobile-row">
                    <div className="mobile-label">Women</div>
                    <div className="mobile-value">{formatPercent(stats?.womenPct)}</div>
                  </div>
                  <div className="mobile-row">
                    <div className="mobile-label">International</div>
                    <div className="mobile-value">{formatPercent(stats?.internationalPct)}</div>
                  </div>

                  <div className="mobile-section-title">Deferred Program Details</div>

                  <div className="mobile-row">
                    <div className="mobile-label">Typical audience</div>
                    <div className={`mobile-value ${textDiffTypicalAudience[idx]}`}>{format(p?.typicalAudience)}</div>
                  </div>
                  <div className="mobile-row">
                    <div className="mobile-label">Deferral window</div>
                    <div className={`mobile-value ${textDiffDeferralWindow[idx]}`}>{format(p?.deferralWindow)}</div>
                  </div>
                  <div className="mobile-row">
                    <div className="mobile-label">Test policy</div>
                    <div className={`mobile-value ${textDiffTestPolicy[idx]}`}>{format(p?.gmatGre)}</div>
                  </div>
                  <div className="mobile-row">
                    <div className="mobile-label">Application timing</div>
                    <div className={`mobile-value ${textDiffApplicationTiming[idx]}`}>{format(p?.applicationTiming)}</div>
                  </div>
                  <div className="mobile-row">
                    <div className="mobile-label">Interview</div>
                    <div className={`mobile-value ${textDiffInterview[idx]}`}>{format(p?.interview)}</div>
                  </div>

                  <div className="mobile-row mobile-notes">
                    <div className="mobile-label">Notes</div>
                    <div className="mobile-value">
                      <ul className="comparison-notes-list">
                        {formatNotes(p?.notes).map((n) => (
                          <li key={n}>{n}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </section>
            );
          })}
        </div>

        <section className="why-section" aria-label="Why this school">
          <h2 className="why-title">Why This School</h2>
          <div className="why-grid">
            {selectedProgramsBySlot.map((p, idx) => {
              const header = columnHeaderLabel(p, idx);
              const body = p ? WHY_THIS_SCHOOL[p.school] : null;
              return (
                <article key={`why-${idx}`} className="why-card" aria-label={header}>
                  <div className="why-card-header">{header}</div>
                  <p className="why-card-body">{body ?? DASH}</p>
                </article>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default SchoolComparison;
